import React, { useState, useContext, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import './HomePage.css';
import {
  Cpu,
  PlayCircle,
  Bell,
  Activity,
  ArrowRight,
  Layers,
  Users,
  FileText,
  Gift,
  X,
 
} from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import useData from '../hooks/useData';
import { useSubscription } from '../context/SubscriptionContext';

export default function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useContext(AuthContext);
  const API = process.env.REACT_APP_API_URL || 'https://api.baobrain.com';

  const [redirectDecisionMade, setRedirectDecisionMade] = useState(false);
  const [shouldShowContent, setShouldShowContent] = useState(false);
  const [showTrialBanner, setShowTrialBanner] = useState(false);
  
  const { isFirstTimeUser, subscriptionData, loading: subscriptionLoading } = useSubscription();
  const redirectedRef = useRef(false);

  // Handle URL params for site setup
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlShop = params.get('shop');
    const urlSiteId = params.get('site_id');
    const urlSiteToken = params.get('site_token');

    if (urlSiteId && urlSiteToken) {
      sessionStorage.setItem('site_id', urlSiteId);
      sessionStorage.setItem('site_token', urlSiteToken);
      if (urlShop) sessionStorage.setItem('shop_domain', urlShop);
    }
  }, [location.search]);

  // Use cached data for sites
  const { data: sites, loading: sitesLoading, error: sitesError } = useData(
    token ? `sites:${token}` : 'noop',
    () => fetch(`${API}/api/sites`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => {
      if (!r.ok) throw new Error(r.status);
      return r.json();
    }),
    { ttlMs: 24 * 60 * 60 * 1000, deps: [token] }
  );

  // Redirect logic
  useEffect(() => {
    if (redirectDecisionMade || redirectedRef.current) return;
    if (!token) return;
    if (sitesLoading) return;
    
    if (sitesError) {
      setRedirectDecisionMade(true);
      setShouldShowContent(true);
      return;
    }
    
    if (!Array.isArray(sites)) return;

    if (sites.length === 0) {
      redirectedRef.current = true;
      navigate('/how-it-works', { replace: true, state: { from: 'home' } });
      return;
    }

    const storedSiteId = sessionStorage.getItem('site_id');
    const storedSiteToken = sessionStorage.getItem('site_token');
    
    if (!storedSiteId || storedSiteId === 'null' || !storedSiteToken || storedSiteToken === 'null') {
      const firstSite = sites[0];
      if (firstSite) {
        sessionStorage.setItem('site_id', String(firstSite.id));
        if (!storedSiteToken || storedSiteToken === 'null') {
          sessionStorage.setItem('site_token', 'configured');
        }
        sessionStorage.setItem('site_platform', firstSite.platform);
        sessionStorage.setItem('site_url', firstSite.url);
      }
    } else {
      const currentSite = sites.find(s => String(s.id) === String(storedSiteId)) || sites[0];
      if (currentSite) {
        sessionStorage.setItem('site_platform', currentSite.platform);
        sessionStorage.setItem('site_url', currentSite.url);
      }
    }
    
    setRedirectDecisionMade(true);
    setShouldShowContent(true);
  }, [token, sitesLoading, sites, sitesError, navigate, redirectDecisionMade]);

  // Load GA status after content decision
  const { data: gaStatusData, loading: gaLoading } = useData(
    token && shouldShowContent ? `integrationStatus:${token}` : 'noop',
    () => fetch(`${API}/integrations/status`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      if (!res.ok) throw new Error(`Status ${res.status}`);
      return res.json();
    }),
    { ttlMs: 24 * 60 * 60 * 1000, deps: [token, shouldShowContent] }
  );

  // Trial banner logic
  useEffect(() => {
    if (!shouldShowContent) return;
    if (subscriptionLoading) return;
    
    const dismissed = localStorage.getItem('trial_banner_dismissed');
    const enforcementEnabled = process.env.REACT_APP_ENFORCE_SUBSCRIPTIONS === 'true';
    
    if (enforcementEnabled && isFirstTimeUser && !dismissed) {
      setShowTrialBanner(true);
    } else {
      setShowTrialBanner(false);
    }
  }, [isFirstTimeUser, subscriptionData, subscriptionLoading, shouldShowContent]);

  const gaStatus = useMemo(() => {
    if (gaLoading) return { loading: true, connected: false };
    return { loading: false, connected: gaStatusData?.connected || false };
  }, [gaLoading, gaStatusData]);

  const handleDismissTrialBanner = () => {
    setShowTrialBanner(false);
    localStorage.setItem('trial_banner_dismissed', 'true');
  };

  const handleStartTrial = () => {
    navigate('/settings/billing');
  };

  // Loading state
  if (!shouldShowContent) {
    return (
      <Layout hideSidebar>
        <div className="homepage page-shell">
          <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '60vh',
            gap: '24px'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(106, 13, 173, 0.1) 0%, rgba(0, 168, 169, 0.1) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Activity 
                style={{ 
                  animation: 'spin 1s linear infinite',
                  width: '32px',
                  height: '32px',
                  color: '#6a0dad'
                }} 
              />
            </div>
            <p style={{ 
              fontSize: '1rem', 
              color: '#64748b',
              margin: 0,
              fontWeight: 500
            }}>
              {sitesLoading ? 'Loading your sites...' : 'Setting up your dashboard...'}
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout hideSidebar>
      <div className="homepage page-shell">
        {/* Trial Banner */}
        {showTrialBanner && (
          <div style={{
            background: 'linear-gradient(135deg, #6a0dad 0%, #00a8a9 100%)',
            color: 'white',
            padding: '24px',
            borderRadius: '16px',
            position: 'relative',
            boxShadow: '0 8px 24px rgba(106, 13, 173, 0.2)',
            marginBottom: '24px',
            overflow: 'hidden'
          }}>
            {/* Decorative element */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '200px',
              height: '200px',
              background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
              pointerEvents: 'none'
            }} />
            
            <button
              onClick={handleDismissTrialBanner}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'rgba(255,255,255,0.15)',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'white',
                transition: 'all 0.2s',
                backdropFilter: 'blur(4px)'
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
              aria-label="Dismiss banner"
            >
              <X size={16} />
            </button>
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '20px',
              flexWrap: 'wrap',
              paddingRight: '48px',
              position: 'relative',
              zIndex: 1
            }}>
              <div style={{
                background: 'rgba(255,255,255,0.15)',
                borderRadius: '50%',
                padding: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                backdropFilter: 'blur(4px)'
              }}>
                <Gift size={28} />
              </div>
              
              <div style={{ flex: 1, minWidth: '280px' }}>
                <h2 style={{ 
                  margin: '0 0 6px 0', 
                  fontSize: '1.35rem', 
                  fontWeight: '700',
                  letterSpacing: '-0.02em'
                }}>
                  Start Your 14-Day Free Trial
                </h2>
                <p style={{ 
                  margin: 0, 
                  fontSize: '0.95rem', 
                  opacity: 0.95, 
                  lineHeight: '1.5' 
                }}>
                  Get full access to all premium features. No credit card required.
                </p>
              </div>
              
              <button
                onClick={handleStartTrial}
                style={{
                  background: 'white',
                  color: '#6a0dad',
                  border: 'none',
                  padding: '14px 28px',
                  borderRadius: '10px',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  flexShrink: 0
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                }}
              >
                Start Free Trial <ArrowRight size={18} />
              </button>
            </div>
          </div>
        )}

        {/* GA4 Connection Banner */}
        {!gaStatus.loading && !gaStatus.connected && (
          <div className="ga4-banner">
            <p>
              <strong>Google Analytics 4 not connected:</strong> Connect now to gain deeper insights.
            </p>
            <button className="ga4-button" onClick={() => navigate('/integrations')}>
              Connect GA4
            </button>
          </div>
        )}

        {/* Welcome Header */}
        <header className="welcome-header">
          <div className="welcome-content">
            <div className="status-badge">
              <Activity className="status-icon" />
              <span>System Active</span>
            </div>
            <h1>Welcome back to BaoBrain</h1>
            <p>Your analytics dashboard is ready. Here's what's happening with your website.</p>
          </div>
          <div className="welcome-actions">
            <button
              className="btn btn-primary btn-large"
              onClick={() => navigate('/dashboard')}
            >
              View Full Dashboard
              <ArrowRight size={18} />
            </button>
          </div>
        </header>

        <div className="dashboard-grid">
          {/* Features Overview */}
          <section className="features-overview">
            <div className="features-header">
              <h2>Capabilities at a glance</h2>
              <p>Everything you need to understand and optimize user experience</p>
            </div>
            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-icon-wrapper">
                  <Cpu className="feature-icon" />
                </div>
                <h4>AI-Powered Insights</h4>
                <p>Deep AI-powered insights that reveal hidden patterns in user behavior</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon-wrapper">
                  <PlayCircle className="feature-icon" />
                </div>
                <h4>Session Replays</h4>
                <p>Full session replays to see exactly how users interact with your site</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon-wrapper">
                  <Layers className="feature-icon" />
                </div>
                <h4>Funnel Analysis</h4>
                <p>Track user journeys across key pages to identify drop-off points</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon-wrapper">
                  <Users className="feature-icon" />
                </div>
                <h4>Visitor Segmentation</h4>
                <p>Group users by behavior, device, and location to tailor experiences</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon-wrapper">
                  <FileText className="feature-icon" />
                </div>
                <h4>Custom Reports</h4>
                <p>Customizable reports & exports tailored to your business needs</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon-wrapper">
                  <Bell className="feature-icon" />
                </div>
                <h4>Real-time Alerts</h4>
                <p>Real-time alerts & integrations to keep you informed of changes</p>
              </div>
            </div>
          </section>

          {/* Call To Action */}
          <section className="action-cta">
            <div className="cta-content">
              <h2>Start Improving Your Website!</h2>
              <p>Take action on the insights you're gathering and optimize your user experience.</p>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
}